import { ApplicationConfig, inject, isDevMode, provideAppInitializer, provideZonelessChangeDetection } from '@angular/core';
import { provideServiceWorker } from '@angular/service-worker';
import { provideRouter, withPreloading, PreloadAllModules, withInMemoryScrolling } from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldDefaultOptions } from '@angular/material/form-field';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS, MatSnackBarConfig } from '@angular/material/snack-bar';
import { MatPaginatorIntl } from '@angular/material/paginator';

import { routes } from '@app/app.routes';
import { PROVEDORES_DATA_BR, registrarLocalePtBr } from '@shared/util/data-br.providers';
import { paginadorEmPortugues } from '@shared/ui/list/paginador-intl';
import { initPendingInterceptorNavigation, pendingInterceptorFn } from '@shared/ui/loading-indicator/pending-interceptor.fn';
import { authInterceptorFn } from '@core/auth/auth.interceptor';
import { versaoInterceptorFn } from '@core/pwa/versao.interceptor';
import { AtualizacaoAppService } from '@core/pwa/atualizacao-app.service';

registrarLocalePtBr();

export const appConfig: ApplicationConfig = {
  providers: [
    // Sem zone.js: a detecção de mudanças passa a ser disparada pelos sinais, pelos eventos de
    // template e pelo async pipe — não mais por qualquer callback assíncrono da aplicação.
    provideZonelessChangeDetection(),
    provideRouter(
      routes,
      withPreloading(PreloadAllModules),
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled'
      })
    ),
    provideAnimationsAsync(),
    provideHttpClient(
      // A ordem importa pouco entre eles, mas o de versão vem primeiro para carimbar a
      // requisição antes de qualquer outro clonar o request.
      withInterceptors([versaoInterceptorFn, authInterceptorFn, pendingInterceptorFn])
    ),
    provideAppInitializer(() => initPendingInterceptorNavigation()),
    /**
     * Service worker: instalável e com o app shell em cache (ngsw-config.json).
     *
     * Desligado em desenvolvimento — um service worker servindo o localhost:4200 do cache faria o
     * dev-server parecer não recompilar.
     *
     * `registerWhenStable:30000` adia o registro para depois de a aplicação estabilizar: registrar
     * no boot faria o download do worker disputar banda com o primeiro carregamento da tela.
     */
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
    provideAppInitializer(() => inject(AtualizacaoAppService).vigiar()),
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        appearance: 'fill'
      } as MatFormFieldDefaultOptions
    },
    // A UI é toda em português; o paginador do Material não é, e aparece em todas as listas.
    { provide: MatPaginatorIntl, useFactory: paginadorEmPortugues },
    // Datas em pt-BR na aplicação inteira — ver PROVEDORES_DATA_BR.
    ...PROVEDORES_DATA_BR,
    {
      provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
      useValue: {
        duration: 5000,
        horizontalPosition: 'end',
        verticalPosition: 'bottom'
      } as MatSnackBarConfig
    }
  ]
};
